export interface IGstBreakdownLine {
  rate: number;
  amount: number;
}

interface IGstTaxedLine {
  taxPercentRate: number;
  taxTotal?: { amount: number } | null;
}

/** Converts a persisted tax rate fraction (e.g. 0.18) to a percent (e.g. 18). */
export const toGstPercent = (taxPercentRate: number): number => Math.round(taxPercentRate * 10000) / 100;

/**
 * Splits India GST by rate: line taxes are grouped by each line's rate, shipping GST joins the highest
 * line rate, and the handling fee GST (same formula as the backend: round(handlingTotal × rate ÷ 100))
 * joins its own rate bucket.
 */
export const getGstBreakdown = (
  lines: IGstTaxedLine[],
  shippingTax: number,
  handlingTotal: number,
  handlingFeeGstRate: number,
): IGstBreakdownLine[] => {
  if (!lines.length) {
    return [];
  }

  const buckets = new Map<number, number>();
  for (const line of lines) {
    const rate = toGstPercent(line.taxPercentRate);
    buckets.set(rate, (buckets.get(rate) ?? 0) + (line.taxTotal?.amount ?? 0));
  }

  if (shippingTax > 0) {
    const highestRate = Math.max(...buckets.keys());
    buckets.set(highestRate, (buckets.get(highestRate) ?? 0) + shippingTax);
  }

  const handlingFeeTax = Math.round((handlingTotal * handlingFeeGstRate) / 100);
  if (handlingFeeTax > 0) {
    buckets.set(handlingFeeGstRate, (buckets.get(handlingFeeGstRate) ?? 0) + handlingFeeTax);
  }

  return [...buckets.entries()]
    .filter(([, amount]) => amount !== 0)
    .sort(([rateA], [rateB]) => rateA - rateB)
    .map(([rate, amount]) => ({ rate, amount }));
};
